import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../context";
import Rotate from "./Rotate";
import CarDamageSelector, { CAR_MARKERS } from "../utils_new/CarDamageSelector";
import {
  pdfUpload,
  patLogs,
  saveCollisionForm,
  setPortoModuleComplete,
} from "../context/utils";

/*
 * DamageQuestionnaire
 * -------------------
 * A three-step first-notice-of-loss module, run as a menu block:
 *
 *   1. What hit the car     -- one cause out of eight tiles.
 *   2. Where the damage is  -- the top-down diagram from CarDamageSelector,
 *                              with the parts the claimant marked listed in a
 *                              card underneath it.
 *   3. A few last things    -- the closing questions, then free-text comments
 *                              and an optional PDF across the full width.
 *
 * It runs landscape, like the rest of the inspection flow: the lock the flow is
 * already under is left alone, and a phone held upright gets the same Rotate
 * prompt every other module shows. The layout is built for a short, wide
 * viewport -- a card that fills the screen, with only its middle scrolling -- so
 * the header and the one primary action are always in view.
 *
 * Its styles live in index.css, under the "Damage Questionnaire" banner near
 * the end of that file. Every rule there is prefixed dq- and sits inside
 * .dq-root, which only this module renders.
 *
 * Wiring it up for a client is two lines: add the block to config["blocks"]
 * with "name": "Damage Questionnaire", and the SelectPage switch in App.js
 * routes to it. Every label below is a default; a client overrides any of them
 * through config["language"], and the cause tiles and questions themselves
 * through config["damage-questionnaire"].
 */

const STEP_COUNT = 3;

const DEFAULT_CAUSES = [
  { id: "heavy_vehicle", label: "Heavy vehicle", icon: "heavy" },
  { id: "light_vehicle", label: "Light vehicle", icon: "light" },
  { id: "person_or_animal", label: "Person or animal", icon: "person" },
  { id: "fixed_object", label: "Fixed object", icon: "pole" },
  { id: "falling_object", label: "Falling object", icon: "falling" },
  { id: "fire_or_weather", label: "Fire or weather", icon: "fire" },
  { id: "theft_or_vandalism", label: "Theft or vandalism", icon: "theft" },
  { id: "other", label: "Other", icon: "other" },
];

/**
 * `key` is the name the answer is stored under in the claim data (see
 * buildClaimData), and `lowercase` stores it in lower case -- "yes" rather than
 * the "Yes" on the button, to match pdf_upload. `exclusive` is the option that
 * stands alone: ticking "None" clears the rest, and ticking anything else
 * clears "None".
 */
const DEFAULT_QUESTIONS = [
  {
    id: "drivable",
    key: "vehicle_driven",
    text: "Can the vehicle still be driven?",
    options: ["Yes", "No"],
    multi: false,
    lowercase: true,
  },
  {
    id: "airbags",
    key: "airbag_deploy",
    text: "Did any airbags deploy?",
    options: ["Driver", "Passenger", "Curtain", "None"],
    multi: true,
    exclusive: "None",
  },
  {
    id: "glass",
    key: "glass_damaged",
    text: "Any glass damaged?",
    options: [
      "Windshield",
      "Rear windshield",
      "Left window",
      "Right window",
      "Sunroof",
      "None",
    ],
    multi: true,
    exclusive: "None",
  },
];

/** Default cap on the attachment, in MB. config can raise or lower it. */
const DEFAULT_MAX_FILE_MB = 5;

/**
 * The tile artwork, one line-drawing per cause. Everything inherits
 * currentColor so a selected tile flips the icon along with the label.
 */
function CauseIcon({ name }) {
  const common = {
    width: 34,
    height: 34,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  switch (name) {
    case "heavy":
      return (
        <svg {...common} strokeWidth={1.4}>
          <rect x="2" y="8.5" width="8" height="4.5" rx="1.4" />
          <path d="M3.4 8.5 L4.8 6 H7.4 L8.8 8.5" />
          <circle cx="4.4" cy="14.4" r="1" />
          <circle cx="7.8" cy="14.4" r="1" />
          <rect x="12" y="7" width="5.5" height="6" rx="1" />
          <path d="M17.5 9 H20 L21.5 11 V13 H17.5 Z" />
          <circle cx="14" cy="15" r="1.1" />
          <circle cx="20" cy="15" r="1.1" />
        </svg>
      );
    case "light":
      return (
        <svg {...common}>
          <circle cx="6" cy="16" r="3.4" />
          <circle cx="18" cy="16" r="3.4" />
          <polyline points="6 16 10 9 16 9 18 16" />
          <line x1="13" y1="9" x2="15" y2="6" />
        </svg>
      );
    case "person":
      return (
        <svg {...common}>
          <circle cx="12" cy="5" r="2.4" />
          <line x1="12" y1="8" x2="12" y2="15" />
          <polyline points="8 20 12 15 16 20" />
          <line x1="8" y1="11" x2="16" y2="11" />
        </svg>
      );
    case "pole":
      return (
        <svg {...common}>
          <line x1="12" y1="9" x2="12" y2="21" />
          <circle cx="12" cy="6" r="3.2" />
          <line x1="8" y1="21" x2="16" y2="21" />
        </svg>
      );
    case "falling":
      return (
        <svg {...common}>
          <rect
            x="9"
            y="3.5"
            width="6"
            height="6"
            rx="1"
            transform="rotate(12 12 6.5)"
          />
          <line x1="6.5" y1="12.5" x2="6.5" y2="15" />
          <line x1="12" y1="13" x2="12" y2="15.5" />
          <line x1="17.5" y1="12.5" x2="17.5" y2="15" />
          <line x1="4" y1="20" x2="20" y2="20" />
        </svg>
      );
    case "fire":
      return (
        <svg {...common}>
          <path d="M12 3 c2 3 4 4.5 4 8 a4 4 0 0 1 -8 0 c0 -2 1 -3 1.5 -4 c.4 1 1 1.6 1.6 2 c.2 -2.2 -.6 -4.4 -1.1 -6z" />
        </svg>
      );
    case "theft":
      return (
        <svg {...common}>
          <rect x="5" y="11" width="14" height="9" rx="1.6" />
          <path d="M8 11 V8 a4 4 0 0 1 6.9 -2.7" />
          <line x1="13.5" y1="14.5" x2="16" y2="17" />
          <line x1="16" y1="14.5" x2="13.5" y2="17" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="5.5" cy="12" r="1.4" />
          <circle cx="12" cy="12" r="1.4" />
          <circle cx="18.5" cy="12" r="1.4" />
        </svg>
      );
  }
}

/** "Left Front Bumper" -> "left_front_bumper", for ids a client did not give. */
const slug = (text) =>
  String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/**
 * Forces a read result to the data: prefix the backend strips before writing the
 * file to S3 (savepdfDataS3 drops exactly "data:application/pdf;base64,"). A
 * phone that reports no MIME type for its own PDF produces
 * "data:application/octet-stream;base64," or "data:;base64," instead, and those
 * prefixes would otherwise be decoded as part of the file and corrupt it.
 */
const asPdfDataUri = (result) => {
  const text = String(result || "");
  const comma = text.indexOf(",");
  return `data:application/pdf;base64,${comma === -1 ? text : text.slice(comma + 1)}`;
};

/** S3 object keys are built straight from this, so keep it boring. */
const safeKey = (text) =>
  String(text)
    .replace(/[^A-Za-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

/**
 * #rgb / #rrggbb -> rgba(), so one configured brand colour can also supply the
 * pale fills and hairlines around it. Anything else (a named colour, a gradient)
 * is left to the caller's fallback.
 */
const withAlpha = (color, alpha) => {
  if (typeof color !== "string") return null;
  const hex = color.trim().replace("#", "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const n = parseInt(full, 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/** A client may give a cause as a bare string or as a full object. */
const normaliseCauses = (list) => {
  if (!Array.isArray(list) || list.length === 0) return DEFAULT_CAUSES;
  return list
    .map((entry) => {
      if (typeof entry === "string") {
        return { id: slug(entry), label: entry, icon: "other" };
      }
      if (!entry || !entry.label) return null;
      return {
        id: entry.id || slug(entry.label),
        label: entry.label,
        icon: entry.icon || "other",
      };
    })
    .filter(Boolean);
};

const normaliseQuestions = (list) => {
  if (!Array.isArray(list) || list.length === 0) return DEFAULT_QUESTIONS;
  return list
    .map((entry) => {
      if (!entry || !entry.text || !Array.isArray(entry.options)) return null;
      const id = entry.id || slug(entry.text);
      return {
        id,
        key: entry.key || id,
        text: entry.text,
        options: entry.options,
        multi: !!entry.multi,
        exclusive: entry.exclusive || null,
        lowercase: !!entry.lowercase,
      };
    })
    .filter(Boolean);
};

/**
 * The claimant's answers as the backend will store them. It starts as {} and
 * gains keys page by page, in the order they are filled in:
 *
 *   page 1   damage_cause    "Light vehicle"               once a tile is picked
 *   page 2   damage_area     ["Roof", "Rear Glass"]        once page 2 is opened
 *   page 3   vehicle_driven  "yes" | "no"                  once answered
 *            airbag_deploy   ["Driver", "Curtain"]         once answered
 *            glass_damaged   ["None"]                      once answered
 *            description     ""   -> the comment, if any   once page 3 is opened
 *            pdf_upload      "no" -> "yes" once a PDF is attached
 *
 * Values are the untranslated option labels, so the record reads the same for
 * every client whatever language the screen was shown in -- lower-cased for a
 * question marked `lowercase`, as vehicle_driven is. Going back a page
 * keeps what was already given; changing an answer updates it in place.
 *
 * `reached` is the furthest page opened, which is what brings each page's keys
 * in -- page 2's list is there (possibly empty) as soon as that page is shown,
 * and page 3's two defaults the same.
 */
export function buildClaimData({
  cause,
  areas,
  reached,
  questions,
  answers,
  notes,
  hasPdf,
}) {
  const data = {};
  if (cause) data.damage_cause = cause.label;

  if (reached >= 2) data.damage_area = areas.map((area) => area.label);

  if (reached >= 3) {
    questions.forEach((question) => {
      const given = answers[question.id];
      const store = (value) =>
        question.lowercase ? String(value).toLowerCase() : value;
      if (question.multi) {
        if (Array.isArray(given) && given.length > 0) {
          data[question.key] = given.map(store);
        }
      } else if (given) {
        data[question.key] = store(given);
      }
    });
    data.description = notes || "";
    data.pdf_upload = hasPdf ? "yes" : "no";
  }

  return data;
}

/**
 * @param {object}   [props]
 * @param {function} [props.onClaimDataChange] (claimData) => void, called with
 *   the latest claim data every time it changes. Optional: SelectPage mounts the
 *   module without it, and nothing else depends on it being there.
 */
export default function DamageQuestionnaire({ onClaimDataChange } = {}) {
  const {
    config,
    clientId,
    inspectionId,
    currentBlock,
    condition,
    setCondition,
    mandatory,
    setMandatory,
    setScreen,
    isLandscape,
    setLandscape,
  } = useContext(AppContext);

  const settings = (config && config["damage-questionnaire"]) || {};

  const causes = useMemo(
    () => normaliseCauses(settings.causes),
    [settings.causes]
  );
  const questions = useMemo(
    () => normaliseQuestions(settings.questions),
    [settings.questions]
  );
  const allowAttachment = settings.attachment !== false;
  const allowComments = settings.comments !== false;
  const maxFileMb = Number(settings.maxFileSizeMb) > 0
    ? Number(settings.maxFileSizeMb)
    : DEFAULT_MAX_FILE_MB;

  const [step, setStep] = useState(1);
  /** Furthest page opened; going back does not lower it. See buildClaimData. */
  const [reached, setReached] = useState(1);
  const [cause, setCause] = useState(null);
  const [parts, setParts] = useState([]);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState("");
  const [doc, setDoc] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * The flow is already fullscreen and landscape-locked by the time the menu
   * hands over, so nothing is requested here -- a phone held upright gets the
   * Rotate prompt, whose own button does the re-lock inside a user gesture.
   * The listeners are added rather than assigned to window.onresize so they
   * come off again with the screen.
   */
  useEffect(() => {
    const syncOrientation = () =>
      setLandscape(window.innerWidth > window.innerHeight * 1.2);

    syncOrientation();
    patLogs("damage questionnaire opened", inspectionId);

    window.addEventListener("resize", syncOrientation);
    window.addEventListener("orientationchange", syncOrientation);
    return () => {
      window.removeEventListener("resize", syncOrientation);
      window.removeEventListener("orientationchange", syncOrientation);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** config["language"] is the app's translation map; it is a string for some clients. */
  const t = (text) => {
    const lang = config && config["language"];
    if (lang && typeof lang === "object" && Object.keys(lang).includes(text)) {
      return lang[text];
    }
    return text;
  };

  const themeVars = {
    "--dq-accent": config["colors"]["btn"],
    "--dq-accent-ink": config["colors"]["btn"],
    "--dq-accent-soft": withAlpha(config["colors"]["btn"], 0.42) || "rgba(37,99,235,.42)",
    "--dq-accent-line": withAlpha(config["colors"]["btn"], 0.45) || "rgba(37,99,235,.45)",
    "--dq-tint": withAlpha(config["colors"]["btn"], 0.1) || "#e7effe",
  };

  /** Marked hotspots, in the order CAR_MARKERS lists them rather than click order. */
  const markedAreas = useMemo(
    () => CAR_MARKERS.filter((marker) => parts.includes(marker.id)),
    [parts]
  );

  const chosenCause = causes.find((c) => c.id === cause) || null;

  /**
   * The JSON the backend gets: {} on arrival, a key more with each answer. It
   * is derived from the screen's own state rather than kept alongside it, so it
   * cannot drift -- clearing a hotspot, swapping the PDF, or going back and
   * picking another cause are all reflected without a second update.
   */
  const claimData = useMemo(
    () =>
      buildClaimData({
        cause: chosenCause,
        areas: markedAreas,
        reached,
        questions,
        answers,
        notes,
        hasPdf: !!doc,
      }),
    [chosenCause, markedAreas, reached, questions, answers, notes, doc]
  );

  useEffect(() => {
    if (onClaimDataChange) onClaimDataChange(claimData);
  }, [claimData, onClaimDataChange]);

  const isAnswered = (question) => {
    const given = answers[question.id];
    return question.multi
      ? Array.isArray(given) && given.length > 0
      : !!given;
  };

  const canProceed =
    step === 1
      ? !!cause
      : step === 2
      ? parts.length > 0
      : questions.every(isAnswered);

  const titles = {
    1: t("How did the damage happen?"),
    2: t("Where is the damage?"),
    3: t("A few last things"),
  };

  const hints = {
    1: chosenCause
      ? `${t("Selected")}: ${t(chosenCause.label)}`
      : t("Pick the one that hit you."),
    2: parts.length
      ? `${parts.length} ${
          parts.length === 1 ? t("area marked") : t("areas marked")
        }`
      : t("Tap + on each damaged area of the car."),
    3: canProceed
      ? t("Ready to file.")
      : t("Please answer every question to continue."),
  };

  const pickCause = (id) => {
    setCause(id);
    patLogs(`damage cause selected: ${id}`, inspectionId);
  };

  const toggleAnswer = (question, value) => {
    setAnswers((prev) => {
      if (!question.multi) return { ...prev, [question.id]: value };

      let picked = Array.isArray(prev[question.id]) ? [...prev[question.id]] : [];
      if (question.exclusive && value === question.exclusive) {
        // "None" stands alone: ticking it drops everything else, untick clears.
        picked = picked.includes(value) ? [] : [value];
      } else {
        picked = picked.filter((v) => v !== question.exclusive);
        picked = picked.includes(value)
          ? picked.filter((v) => v !== value)
          : [...picked, value];
      }
      return { ...prev, [question.id]: picked };
    });
  };

  const isPicked = (question, value) => {
    const given = answers[question.id];
    return question.multi
      ? Array.isArray(given) && given.includes(value)
      : given === value;
  };

  /**
   * A phone's file picker is not as tidy as the desktop one.
   *
   * A PDF handed over by an Android content:// provider often arrives with an
   * empty or generic type, so the extension is taken as a fallback rather than
   * refusing a file the claimant can see is a PDF. The input is also left alone
   * until the read has finished -- clearing it while the File is still being
   * read loses the file on some Android WebViews -- and reset afterwards so the
   * same file can be picked twice in a row.
   */
  const onFilePick = (event) => {
    const input = event.target;
    const file = input.files && input.files[0];
    if (!file) return;

    const reset = () => {
      try {
        input.value = "";
      } catch (e) {
        /* older WebViews refuse this; picking the same file twice just no-ops */
      }
    };

    const type = (file.type || "").toLowerCase();
    const isPdf =
      type === "application/pdf" ||
      ((type === "" || type === "application/octet-stream") &&
        /\.pdf$/i.test(file.name || ""));

    if (!isPdf) {
      setDoc(null);
      setFileError(t("Please attach a PDF file."));
      reset();
      return;
    }
    if (file.size > maxFileMb * 1024 * 1024) {
      setDoc(null);
      setFileError(`${t("The file must be smaller than")} ${maxFileMb} MB.`);
      reset();
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFileError(null);
      setDoc({ name: file.name, data: asPdfDataUri(reader.result) });
      reset();
    };
    reader.onerror = () => {
      setDoc(null);
      setFileError(t("That file could not be read. Please try another one."));
      reset();
    };
    reader.readAsDataURL(file);
  };

  const backToMenu = () => {
    patLogs("damage questionnaire left for the menu", inspectionId);
    setScreen("menu");
  };

  /**
   * Two things leave this screen: the answers, which go to
   * estimate_form_data.collision_data as the claim-data JSON, and the PDF, which
   * goes to S3 on its own rather than riding along as base64 beside them.
   *
   * Neither is awaited, so the claimant is not held here; a save that does not
   * land is logged against the inspection instead of being lost.
   */
  const submit = () => {
    if (!canProceed || submitting) return;
    setSubmitting(true);

    if (allowAttachment && doc) {
      const blockTag = currentBlock["tag"] || currentBlock["id"] || "damage";
      pdfUpload(
        clientId,
        inspectionId,
        { pdfData: doc.data },
        `${safeKey(blockTag)}_attachment`
      );
    }

    saveCollisionForm(claimData, inspectionId).then((status) => {
      if (status !== "success") {
        patLogs(`collision form not saved: ${status}`, inspectionId);
      }
    });

    if (currentBlock["mandatory"]) {
      setMandatory({ ...mandatory, [currentBlock["id"]]: "completed" });
    }
    setCondition({ ...condition, [currentBlock["id"]]: "completed" });
    setPortoModuleComplete(currentBlock["tag"]);
    patLogs(`${currentBlock["id"]} is completed`, inspectionId);
    setScreen("menu");
  };

  const goBack = () => {
    if (step === 1) {
      backToMenu();
      return;
    }
    setStep(step - 1);
  };

  const goNext = () => {
    if (!canProceed) return;
    if (step < STEP_COUNT) {
      setStep(step + 1);
      setReached((furthest) => Math.max(furthest, step + 1));
      return;
    }
    submit();
  };

  return (
    <div className="dq-root" style={themeVars}>
      <div className="dq-shell">
        <div className="dq-card">
          <div className="dq-head">
            <div className="dq-head-titles">
              <span className="dq-title">{titles[step]}</span>
              <span className="dq-step">
                {t("Step")} {step} {t("of")} {STEP_COUNT}
              </span>
            </div>
          </div>

          <div className="dq-body">
            {step === 1 && (
              <>
                <p className="dq-lede">
                  {t("Another vehicle, a road user, an object, or an event")}
                </p>
                <div className="dq-causes">
                  {causes.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      aria-pressed={cause === entry.id}
                      className={`dq-cause ${cause === entry.id ? "is-on" : ""}`}
                      onClick={() => pickCause(entry.id)}
                    >
                      <span className="dq-cause-icon">
                        <CauseIcon name={entry.icon} />
                      </span>
                      <span className="dq-cause-label">{t(entry.label)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {step === 2 && (
              <div className="dq-areas">
                <div className="dq-car">
                  <CarDamageSelector
                    selectedIds={parts}
                    onSelectionChange={setParts}
                    onMarkerClick={(marker, isNowSelected) =>
                      patLogs(
                        `damage area ${marker.id} ${
                          isNowSelected ? "marked" : "cleared"
                        }`,
                        inspectionId
                      )
                    }
                    maxWidth={780}
                    /* Overrides the component's own width-first sizing: in a box
                       of known height the viewBox scales to fit it instead. */
                    style={{ height: "100%", width: "auto", maxWidth: "100%" }}
                  />
                </div>

                {/* The card the diagram feeds: what has been marked, by name. */}
                <div className="dq-panel">
                  <div className="dq-panel-head">
                    <span className="dq-panel-title">{t("Selected areas")}</span>
                    <span
                      className="dq-panel-count"
                      style={{
                        color: markedAreas.length
                          ? "var(--dq-accent-ink)"
                          : "rgba(15,23,42,.42)",
                      }}
                    >
                      {markedAreas.length} {t("marked")}
                    </span>
                  </div>

                  {markedAreas.length === 0 ? (
                    <p className="dq-empty">
                      {t("Tap + on each damaged area of the car.")}
                    </p>
                  ) : (
                    <div className="dq-chips">
                      {markedAreas.map((area) => (
                        <span key={area.id} className="dq-chip">
                          {t(area.label)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <>
                {questions.map((question, index) => (
                  <div className="dq-q" key={question.id}>
                    <div className="dq-q-text">
                      <span className="dq-q-num">{index + 1}.</span>
                      {t(question.text)}
                      {/* Every question has to be answered before Submit enables. */}
                      <span className="dq-req" aria-hidden="true">
                        *
                      </span>
                    </div>
                    <div className="dq-opts">
                      {question.options.map((option) => (
                        <button
                          key={option}
                          type="button"
                          aria-pressed={isPicked(question, option)}
                          className={`dq-opt ${
                            isPicked(question, option) ? "is-on" : ""
                          }`}
                          onClick={() => toggleAnswer(question, option)}
                        >
                          {t(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Full width, under the questions, rather than a side panel. */}
                {(allowComments || allowAttachment) && (
                  <div className="dq-support">
                    <div className="dq-panel-head">
                      <span className="dq-panel-title">
                        {t("Supporting details")}
                      </span>
                    </div>
                    <div className="dq-support-grid">
                      {allowComments && (
                        <div>
                          <div className="dq-field-label">
                            {t("Additional comments")}
                          </div>
                          <textarea
                            className="dq-textarea"
                            value={notes}
                            placeholder={t(
                              "Anything more about how the damage happened"
                            )}
                            onChange={(event) => setNotes(event.target.value)}
                          />
                        </div>
                      )}

                      {allowAttachment && (
                        <div>
                          <div className="dq-field-label">
                            {t("Attachment")}
                          </div>
                          <label className={`dq-attach ${doc ? "is-on" : ""}`}>
                            <span className="dq-attach-icon" aria-hidden="true">
                              {doc ? "✓" : "+"}
                            </span>
                            <span className="dq-attach-text">
                              <span className="dq-attach-name">
                                {doc ? t("Document attached") : t("Attach a PDF")}
                              </span>
                              <span className="dq-attach-sub">
                                {doc
                                  ? doc.name
                                  : t("Claim form or police report")}
                              </span>
                            </span>
                            <input
                              type="file"
                              accept="application/pdf,.pdf"
                              onChange={onFilePick}
                            />
                          </label>
                          {fileError && (
                            <p className="dq-file-error">{fileError}</p>
                          )}
                          {doc && (
                            <button
                              type="button"
                              className="dq-drop-attach"
                              onClick={() => {
                                setDoc(null);
                                setFileError(null);
                              }}
                            >
                              {t("Remove file")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="dq-foot">
            <button type="button" className="dq-back" onClick={goBack}>
              {t("Back")}
            </button>
            <div className={`dq-hint ${canProceed ? "" : "is-todo"}`}>
              {hints[step]}
            </div>
            <button
              type="button"
              className="dq-next"
              disabled={!canProceed || submitting}
              onClick={goNext}
            >
              {step === STEP_COUNT ? t("Submit") : t("Continue")}
            </button>
          </div>
        </div>
      </div>

      {/* Laid over the screen rather than swapped in for it. Returning <Rotate />
          instead would unmount this subtree -- and with it the file input, which
          Android leaves waiting for its own picker while the page behind turns
          portrait. The picked file then had nothing to fire a change on. */}
      {!isLandscape && (
        <div className="dq-rotate">
          <Rotate />
        </div>
      )}
    </div>
  );
}
